(ns journal-app-client.components.input
  (:require [journal-app-client.components.button :as button]
            [journal-app-client.utils.common :as common]
            [journal-app-client.utils.formatting :as fmt]
            [journal-app-client.utils.react :refer [use-state]]
            [journal-app-client.utils.interop :as interop]
            [journal-app-client.translations :refer [tr]]
            [tick.core :as t]
            [reagent.core :as r]
            [re-frame.core :as rf]))

(defonce variables (r/atom nil))

(defn input-regex [props]
  (let [regex-type (or (:regex-type props) props)
        regex (:regex props)]
    (case regex-type
      :alphanumeric #"^[a-zA-Z0-9]{1,50}$"
      :capital-alphanumeric #"^[A-Z0-9]{1,50}$"
      :capital-alphanumeric-with-hyphen #"^[A-Z0-9-]{1,50}$"
      :number #"^[0-9.-]{2,50}$"
      :email #"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$"
      regex)))

(defn validate-user-input [input regex-info]
  (if (and (not (nil? input)) (string? input))
    (string? (first (re-seq (input-regex regex-info) input)))))

(rf/reg-event-fx
  :keyboard-button-press
  (fn [{:keys [db]} [_ props]]
    (let [keypad-id (:keypad-id props)
          input (:input props)
          regex-props (:regex-props props)
          input-max-length (:input-max-length props)
          key-press-event-fn (:key-press-event-fn props)
          regex-result (when regex-props (validate-user-input input regex-props))
          input-max-length (or input-max-length 50)]
      (if (>= input-max-length (count input))
        (merge
          {:db (cond
                 key-press-event-fn
                 (-> (assoc-in db [:keypad keypad-id :input] input)
                   (assoc-in [:keypad keypad-id :input-prompt] nil))
                 regex-props
                 (-> (assoc-in db [:keypad keypad-id :input] input)
                   (assoc-in [:keypad keypad-id :valid-input?] regex-result)
                   (assoc-in [:keypad keypad-id :input-prompt] nil))
                 :else
                 (-> (assoc-in db [:keypad keypad-id :input] input)
                   (assoc-in [:keypad keypad-id :input-prompt] nil)
                   (assoc-in [:keypad keypad-id :valid-input?] nil)))}
          (when (fn? key-press-event-fn)
            (let [function-result (key-press-event-fn
                                    (merge
                                      (dissoc props :key-press-event-fn)
                                      {:keyboard-button-press? true
                                       :regex-result regex-result}))]
              (when (vector? function-result)
                {:fx [[:dispatch function-result]]}))))
        {:db (-> (assoc-in db [:keypad keypad-id :valid-input?] false)
               (assoc-in [:keypad keypad-id :input-prompt] (tr [:keypad-reached-maximum-character-limit])))}))))

(defn is-valid-input-icon [valid?]
  [:i.fas.fade-in
   {:class (if valid? "fa-check" "fa-exclamation-triangle")
    :style {:display (when (nil? valid?) "none")
            :color (if valid? "var(--green)" "var(--red)")}}])

(defn field-control [& [props]]
  [:div.field (:field-props props)
   (into
     [:div.control
      (when (map? props)
        (dissoc props :field-props))]
     (r/children (r/current-component)))])

(defn input [& [props]]
  [:input.input
   (merge (dissoc props
            :active-input?
            :valid-input?
            :on-enter-key)
     {:class     (str (when (and (not (nil? (:valid-input? props))) (:active-input? props))
                        (if (:valid-input? props)
                          " is-success "
                          " is-danger "))
                   (:class props))
      :on-key-up (fn [event]
                   (when (or (= event.key "Enter") (= event.keyCode 13))
                     (when (fn? (:on-enter-key props))
                       ((:on-enter-key props) event))))})])


(defn prompt-message [props prompt]
  (when (and prompt (not (= "" prompt)))
    [:p.input-prompt
     (merge
       (dissoc props :valid-input? :show-prompt? :hide-prompt?)
       {:style (merge {:transform (if (and (:show-prompt? props) (not (:hide-prompt? props)))
                                    "translateY(0) scale(1)"
                                    "translateY(-2rem) scale(0)")}
                 (:style props))})
     prompt]))

(defn field-input [{:keys [size
                           control-props
                           input-props
                           valid-input?
                           active-input?
                           prompt
                           prompt-props
                           icon-left
                           icon-left-props
                           icon-right
                           icon-right-props]}]
  [field-control
   (merge
     control-props
     {:class (str
               (when icon-left
                 " has-icons-left ")
               (when-not (false? icon-right)
                 " has-icons-right ")
               (:class control-props))
      :style (merge {:font-size size}
               (:style control-props))})
   [input
    (merge
      {:active-input? active-input?
       :valid-input? valid-input?}
      input-props
      {:style (merge
                {:font-size size
                 :z-index (when prompt 1)}
                (:style input-props))})]
   (when icon-left
     [(if (:on-click icon-left-props) :button.is-clickable :span)
      (merge
        {:class "icon is-left"}
        icon-left-props)
      (if (string? icon-left)
        [:i {:class icon-left}]
        icon-left)])
   (when-not (false? icon-right)
     [(if (:on-click icon-right-props) :button.is-clickable :span)
      (merge
        icon-right-props
        {:class (str (:class icon-right-props) " icon is-right ")
         :style (merge (when (:on-click icon-right-props) {:pointer-events "auto"})
                  (:style icon-right-props))})
      (if icon-right
        (if (string? icon-right)
          [:i {:class icon-right}]
          icon-right)
        (when active-input?
          [is-valid-input-icon valid-input?]))])
   (when prompt
     [prompt-message
      (merge prompt-props
        {:class (str (:class prompt-props)
                  (cond
                    valid-input?
                    " is-success "
                    (false? valid-input?)
                    " is-danger "
                    (nil? valid-input?)
                    " is-info "
                    :else nil))})
      prompt])])

(rf/reg-sub :input-variables
  (fn [] @variables))

(rf/reg-sub :multi-slider
  :<- [:input-variables]
  (fn [input-variables [_ id]]
    (if id
      (get-in input-variables [:multi-slider id] nil)
      (:multi-slider input-variables))))

(rf/reg-sub :slider
  :<- [:input-variables]
  (fn [input-variables [_ id]]
    (if id
      (get-in input-variables [:slider id] nil)
      (:slider input-variables))))

(defn radio [& [props]]
  (into
    [:label.has-input.panel-block.radio
     {:class (:outer-class props)
      :style (:outer-style props)}
     [:input.radio
      (merge
        (when (map? props) (dissoc props :outer-style :outer-class))
        {:type "radio"})]]
    (r/children (r/current-component))))

;; TODO: Use use-state instead of ratoms everywhere. Subscriptions are too slow for components.
(defn checkbox [& [props]]
  (into
    [:label.has-input.panel-block.checkbox
     {:class (:outer-class props)
      :style (:outer-style props)}
     [:input
      (merge
        (when (map? props) (dissoc props :outer-style :outer-class))
        {:on-key-up (fn [event]
                      (when (fn? (:on-key-up props))
                        ((:on-key-up props) event))
                      (when (or (= event.key "Enter") (= event.keyCode 13))
                        (when (fn? (:on-enter-key props))
                          ((:on-enter-key props) event))))
         :on-change (fn [event]
                      (when (fn? (:on-change props))
                        ((:on-change props) (.-checked (.-target event)) event)))
         :type      "checkbox"})]]
    (r/children (r/current-component))))

(defn switch [& [props]]
  (into
    [checkbox
     (merge
       (when (map? props) (dissoc props :on-enter-key))
       {:class (str (:class props) " is-switch ")
        :on-key-up (fn [event]
                     (when (fn? (:on-key-up props))
                       ((:on-key-up props) event))
                     (when (or (= event.key "Enter") (= event.keyCode 13))
                       (when (fn? (:on-enter-key props))
                         ((:on-enter-key props) event))))})]
    (r/children (r/current-component))))

(defn date-picker-fn [& [props]]
  (let [today (-> (t/new-date) (t/at "00:00"))
        [state set-state] (use-state (let [initial-date-time (or (:value props) (:date props) (:date-time props) today)]
                                       {:date-time (if (t/date-time? initial-date-time)
                                                     initial-date-time
                                                     (if (t/date? initial-date-time)
                                                       (-> initial-date-time (t/at "00:00"))
                                                       (-> (t/new-date) (t/at "00:00"))))}))
        selected-date (:date-time state)
        first-day-of-month (t/<< selected-date (t/new-period (dec (t/day-of-month selected-date)) :days))
        days-in-a-month-prev (t/range (t/<< first-day-of-month (t/new-period 1 :months))
                               first-day-of-month
                               (t/new-duration 1 :days))
        days-in-a-month (t/range first-day-of-month
                          (t/>> first-day-of-month (t/new-period 1 :months))
                          (t/new-duration 1 :days))
        days-in-a-month-next (t/range (t/>> first-day-of-month (t/new-period 1 :months))
                               (t/>> first-day-of-month (t/new-period 2 :months))
                               (t/new-duration 1 :days))
        months ["January" "February" "March" "April" "May" "June" "July" "August" "September" "October" "November" "December"]
        prepend-zero (fn prepend-zero [num] (if (< num 10) (str "0" num) num))
        new-date (fn new-date [date-time]
                   (let [year (t/year date-time)
                         month (t/int (t/month date-time))
                         day (t/day-of-month date-time)]
                     (t/date (str (prepend-zero year) "-" (prepend-zero month) "-" (prepend-zero day)))))]
    [:div
     {:class (:class props)
      :style {:overflow "auto"
              :max-width "34rem"
              :margin-left "auto"
              :margin-right "auto"}}
     [:div
      {:style {:display     "flex"
               :margin      "0 auto"
               :align-items "center"
               :justify-content "space-between"
               :min-width        "19em"}}
      [button/button
       {:style    {:margin           "0.25em"
                   :background-color "transparent"
                   :border           "none"}
        :on-click (fn []
                    (let [date-time (t/<< selected-date (t/new-period 1 :years))]
                      (when (fn? (:on-change props)) ((:on-change props) (new-date date-time)))
                      (set-state (assoc state :date-time date-time))))}
       [:span.icon [:i.fas.fa-angles-left]]]
      [:div {:style {:height           "2em"
                     :width            "1px"
                     :background-color "hsla(var(--text-hsl), 0.1)"}}]
      [button/button
       {:style    {:margin           "0.25em"
                   :background-color "transparent"
                   :border           "none"}
        :on-click (fn []
                    (let [date-time (t/<< selected-date (t/new-period 1 :months))]
                      (when (fn? (:on-change props)) ((:on-change props) (new-date date-time)))
                      (set-state (assoc state :date-time date-time))))}
       [:span.icon [:i.fas.fa-angle-left]]]
      [:div {:style {:height           "2em"
                     :width            "1px"
                     :background-color "hsla(var(--text-hsl), 0.1)"}}]
      [button/button
       {:style    {:margin           "0.25em"
                   :background-color "transparent"
                   :border           "none"
                   :min-width        "6.5em"
                   :max-width        "12em"
                   :flex             "1"
                   :color            "var(--info)"}
        :on-click (fn []
                    (when (fn? (:on-change props)) ((:on-change props) (new-date today)))
                    (set-state (assoc state :date-time today)))}
       [:span (str (nth months (dec (t/int (t/month selected-date)))) " " (t/int (t/year selected-date)))]]
      [:div {:style {:height           "2em"
                     :width            "1px"
                     :background-color "hsla(var(--text-hsl), 0.1)"}}]
      [button/button
       {:style    {:margin           "0.25em"
                   :background-color "transparent"
                   :border           "none"}
        :on-click (fn []
                    (let [date-time (t/>> selected-date (t/new-period 1 :months))]
                      (when (fn? (:on-change props)) ((:on-change props) (new-date date-time)))
                      (set-state (assoc state :date-time date-time))))}
       [:span.icon [:i.fas.fa-angle-right]]]
      [:div {:style {:height           "2em"
                     :width            "1px"
                     :background-color "hsla(var(--text-hsl), 0.1)"}}]
      [button/button
       {:style    {:margin           "0.25em"
                   :background-color "transparent"
                   :border           "none"}
        :on-click (fn []
                    (let [date-time (t/>> selected-date (t/new-period 1 :years))]
                      (when (fn? (:on-change props)) ((:on-change props) (new-date date-time)))
                      (set-state (assoc state :date-time date-time))))}
       [:span.icon [:i.fas.fa-angles-right]]]]
     (into
       [:div
        {:style {:display         "flex"
                 :min-width        "19em"}}]
       (map-indexed
         (fn [index week-string]
           [:div
            {:key   (str week-string "-week-string-" index)
             :style {:display "flex" :flex 1 :justify-content "center"}}
            [:span.button  ;; TODO probably shouldn't use the button class here for styling
             {:style {:width          "2.5em"
                      :height         "2.5em"
                      "--border"      "transparent"
                      "--scheme-main" "transparent"}}
             week-string]])
         ["Mon" "Tue" "Wed" "Thu" "Fri" "Sat" "Sun"]))
     (into
       [:div
        {:style {:min-width        "19em"}}]
       (map-indexed
         (fn [index dates-in-week]
           (into
             [:div
              {:keys  (str "dates-in-week-" index)
               :style {:display         "flex"}}]
             (map-indexed
               (fn [index-2 date-in-week]
                 [:div
                  {:key      (str "date-button-" index-2 "-" index "-" date-in-week)
                   :style {:display "flex" :flex 1 :justify-content "center"}}
                  [button/button
                   {:class    (str (when (and (= (t/day-of-month selected-date) (t/day-of-month date-in-week))
                                           (= (t/month selected-date) (t/month date-in-week)))
                                     "is-info")
                                " is-outlined is-rounded ")
                    :style    {:width          "2.5em"
                               :height         "2.5em"
                               "--border"      "transparent"
                               "--scheme-main" "transparent"
                               :color          (when (or (= (t/month date-in-week) (t/month (first days-in-a-month-prev)))
                                                       (= (t/month date-in-week) (t/month (first days-in-a-month-next))))
                                                 "hsla(var(--text-hsl), 0.33)")}
                    :on-click (fn []
                                (when (fn? (:on-change props)) ((:on-change props) (new-date date-in-week)))
                                (set-state (assoc state :date-time date-in-week)))}
                   [:span (t/day-of-month date-in-week)]]])
               dates-in-week)))
         (partition 7 7
           (concat (take-last (dec (t/int (t/day-of-week (first days-in-a-month)))) days-in-a-month-prev)
             days-in-a-month
             (take (- 7 (t/int (t/day-of-week (last days-in-a-month)))) days-in-a-month-next)))))]))

(defn time-picker-fn [& [props]]
  (let [[state set-state] (use-state (let [initial-time (or (:value props) (:time props) (t/time))
                                           format (or (:format props) :24h)
                                           picker (:picker props)
                                           time-hour (t/hour initial-time)
                                           inner-hours? (and (= :24h format) (> time-hour 12))
                                           minutes (range 0 60)
                                           outer-hours (concat [12] (range 1 12))
                                           inner-hours (concat [0] (range 13 24))
                                           hours (if inner-hours? inner-hours outer-hours)
                                           snap-angles-minutes (map
                                                                 (fn [index] (* index (/ (* 2 Math/PI) (count minutes))))
                                                                 (range 0 (inc (count minutes))))
                                           snap-angles-hours (map
                                                               (fn [index] (* index (/ (* 2 Math/PI) (count hours))))
                                                               (range 0 (inc (count hours))))
                                           initial-minute-index (t/minute initial-time)
                                           initial-hour-index (if (>= time-hour 12)
                                                                (- time-hour 12)
                                                                time-hour)]
                                       {:time   initial-time
                                        :minute {:rotate (nth snap-angles-minutes initial-minute-index (first snap-angles-minutes))
                                                 :index initial-minute-index}
                                        :hour   {:rotate (nth snap-angles-hours initial-hour-index (first snap-angles-hours))
                                                 :index initial-hour-index :inner? inner-hours?}
                                        :picker picker
                                        :format format}))
        item-size 3
        circle-size 15
        inner? (get-in state [(:picker state) :inner?])
        inner-hours? (and (= :24h (:format state)) (get-in state [:hour :inner?]))
        clock-hand-length (if (and (= :24h (:format state)) inner?) (- circle-size (* 2 item-size)) circle-size)
        minutes (range 0 60)
        outer-hours (concat [12] (range 1 12))
        inner-hours (concat [0] (range 13 24))
        hours (if inner-hours? inner-hours outer-hours)
        number-of-items (if (= :hour (:picker state)) (count hours) (count minutes))
        visible-minutes (take-nth 5 minutes)
        visible-number-of-items (count visible-minutes)
        items (if (= :hour (:picker state)) outer-hours visible-minutes)
        snap-angles (map
                      (fn [index] (* index (/ (* 2 Math/PI) number-of-items)))
                      (range 0 (inc number-of-items)))
        angle (/ 360 visible-number-of-items)
        angle-offset 90
        rotate (get-in state [(:picker state) :rotate])
        minute (nth minutes (get-in state [:minute :index]) (first minutes))
        hour (nth hours (get-in state [:hour :index]) (first hours))
        prepend-zero (fn prepend-zero [num] (if (< num 10) (str "0" num) num))
        new-time (fn new-time [hour-index minute-index & [format inner?]]
                   (let [inner? (if (nil? inner?) (get-in state [:hour :inner?]) inner?)
                         minute-index (or minute-index (get-in state [:minute :index]))
                         hour-index (or hour-index (get-in state [:hour :index]))
                         format (or format (:format state))
                         inner-hours? (and (= :24h format) inner?)
                         hours (if inner-hours? inner-hours outer-hours)
                         minute (nth minutes minute-index (first minutes))
                         hour (nth hours hour-index (first hours))
                         hour (if (or (= :AM format) (= :24h format)) hour (if (= hour 12) 0 (+ hour 12)))]
                     (t/time (str (prepend-zero hour) ":" (prepend-zero minute)))))]
    [:div
     {:class (:class props)
      :style (merge {:max-width       "34rem"
                     :margin-left     "auto"
                     :margin-right    "auto"
                     :display         "flex"
                     :align-items     "center"
                     :justify-content "space-evenly"
                     :flex-wrap       "wrap"
                     :font-size       (or (:size props) "max(min(3.25vmin, 1rem), 0.645rem)")}
               (:style props))}
     [:div
      {:style {:display        "flex"
               :flex-direction "column"
               :align-items "center"}}
      [:div
       {:style {:display       "flex"
                :margin-bottom (if (= :24h (:format state)) "1em" "0.5em")}}
       [input
        {:id (str ::hour-time-input)
         :class       (if (= :hour (:picker state))
                        "is-info transparent-selection"
                        "transparent-selection")
         :auto-focus  true
         :style       {:padding     "0"
                       :font-size   "2em"
                       :width       "1.75em"
                       :text-align  "center"
                       :height      "1.75em"
                       :caret-color "transparent"}
         :placeholder "--"
         :value       (prepend-zero hour)
         :on-focus    #(set-state (assoc state :picker :hour :transition? true))
         :on-change   (fn [])
         :on-key-down (fn [event]
                        (let [prev-index (get-in state [:hour :prev-index])
                              cur-index (get-in state [:hour :index])
                              decrease? (or (= 37 (-> event .-keyCode)) (= 40 (-> event .-keyCode)))
                              increase? (or (= 38 (-> event .-keyCode)) (= 39 (-> event .-keyCode)))
                              last-index (dec number-of-items)
                              index (cond increase?
                                          (if (>= cur-index last-index)
                                            0 (inc cur-index))
                                          decrease?
                                          (if (<= cur-index 0)
                                            last-index (dec cur-index))
                                          :else nil)]
                          (when index
                            (let [inner? (if (or (and (= prev-index last-index) (= cur-index 0))
                                               (and (= cur-index 1) (= index 0)))
                                           (not inner-hours?)
                                           inner-hours?)]
                              (when (fn? (:on-change props))
                                ((:on-change props) (new-time index nil nil inner?)))
                              (set-state (assoc state
                                           :hour {:prev-index cur-index
                                                  :index      index
                                                  :rotate     (nth snap-angles index (first snap-angles))
                                                  :inner?     inner?}))))))}]
       [:h1.title
        {:style {:margin      "0 0.0625em 0.25em 0.0625em"
                 :display     "flex"
                 :align-items "center"}}
        ":"]
       [input
        {:id (str ::minute-time-input)
         :class       (if (= :minute (:picker state))
                        "is-info transparent-selection"
                        "transparent-selection")
         :style       {:padding     "0"
                       :font-size   "2em"
                       :width       "1.75em"
                       :text-align  "center"
                       :height      "1.75em"
                       :caret-color "transparent"}
         :placeholder "--"
         :value       (prepend-zero minute)
         :on-focus    #(set-state (assoc state :picker :minute :transition? true))
         :on-change   (fn [])
         :on-key-down (fn [event]
                        (let [cur-index (get-in state [:minute :index])
                              decrease? (or (= 37 (-> event .-keyCode)) (= 40 (-> event .-keyCode)))
                              increase? (or (= 38 (-> event .-keyCode)) (= 39 (-> event .-keyCode)))
                              index (cond increase?
                                          (if (>= cur-index (dec number-of-items))
                                            0 (inc cur-index))
                                          decrease?
                                          (if (<= cur-index 0)
                                            (dec number-of-items) (dec cur-index))
                                          :else nil)]
                          (when index
                            (when (fn? (:on-change props))
                              ((:on-change props) (new-time nil index)))
                            (set-state (assoc state
                                         :minute {:index  index
                                                  :rotate (nth snap-angles index (first snap-angles))})))))}]]
      (when-not (= :24h (:format state))
        [:div.fade-in
         {:style {:display         "flex"
                  :position        "relative"
                  :width           "100%"
                  :margin-bottom   "1em"
                  :justify-content "space-between"}}
         [:div
          {:style {:position         "absolute"
                   :font-size        "2em"
                   :top              "0"
                   :left             (str (if (= :PM (:format state)) "calc(100% - 1.75em)" "0"))
                   :border-radius    "var(--border-radius-medium)"
                   :background-color "hsla(var(--scheme-main-hsl), 1)"
                   :width            "1.75em"
                   :height           "1.25em"
                   :transition       "left calc(2 * var(--transition-duration)) ease"}}]
         [button/button
          {:class    (when (= :AM (:format state)) "is-info is-outlined")
           :style    {:padding          "0"
                      "--border"        "transparent"
                      :background-color "transparent"
                      :font-size        "2em"
                      :width            "1.75em"
                      :height           "1.25em"}
           :on-click (fn []
                       (when (fn? (:on-change props))
                         ((:on-change props) (new-time nil nil :AM)))
                       (when-let [element (if (= :hour (:picker state))
                                            (interop/get-element-by-id (str ::hour-time-input))
                                            (interop/get-element-by-id (str ::minute-time-input)))]
                         (.focus element))
                       (set-state (assoc state :format :AM)))}
          [:span.title
           {:style {:opacity    (if (= :AM (:format state)) "1" "0.5")
                    :font-size  "0.85em"
                    :transition "opacity var(--transition-duration) ease"}}
           "AM"]]
         [button/button
          {:class    (when (= :PM (:format state)) "is-info is-outlined")
           :style    {:padding          "0"
                      "--border"        "transparent"
                      :background-color "transparent"
                      :font-size        "2em"
                      :width            "1.75em"
                      :height           "1.25em"}
           :on-click (fn []
                       (when (fn? (:on-change props))
                         ((:on-change props) (new-time nil nil :PM)))
                       (when-let [element (if (= :hour (:picker state))
                                            (interop/get-element-by-id (str ::hour-time-input))
                                            (interop/get-element-by-id (str ::minute-time-input)))]
                         (.focus element))
                       (set-state (assoc state :format :PM)))}
          [:span.title
           {:style {:opacity    (if (= :PM (:format state)) "1" "0.5")
                    :font-size  "0.85em"
                    :transition "opacity var(--transition-duration) ease"}}
           "PM"]]])]
     (into
       (into
         [:div
          {:style           {:width        (str circle-size "em")
                             :height       (str circle-size "em")
                             :min-width    (str (+ circle-size item-size) "em")
                             :min-height   (str (+ circle-size item-size 1) "em")
                             :margin-right "0"
                             :touch-action "none"
                             :cursor       "pointer"
                             :position     "relative"}
           :draggable       false
           :on-pointer-down (fn [event]
                              (.stopPropagation event)
                              (let [bounding-rect (-> event .-currentTarget .getBoundingClientRect)
                                    x (- (-> event .-clientX) (.-x bounding-rect) (/ (.-width bounding-rect) 2))
                                    y (+ (- (-> event .-clientY)) (.-y bounding-rect) (/ (.-height bounding-rect) 2))
                                    rotate (+ (Math/atan2 (- x) (- y)) Math/PI)
                                    index (Math/round (* (/ rotate (* 2 Math/PI)) number-of-items) 1)
                                    index (if (= index number-of-items) 0 index)
                                    angle (nth snap-angles index (first snap-angles))]
                                (when (and (> (Math/abs y) 0.01) (> (Math/abs x) 0.01))
                                  (let [inner? (< (Math/sqrt (+ (* x x) (* y y)))
                                                 (* 0.66 (/ (.-width bounding-rect) 2)))]
                                    (when (and (or (not (= index (get-in state [(:picker state) :index])))
                                                 (not (= inner? (get-in state [(:picker state) :inner?]))))
                                            (fn? (:on-change props)))
                                      ((:on-change props)
                                       (if (= :minute (:picker state))
                                         (new-time nil index)
                                         (new-time index nil nil inner?))))
                                    (set-state (assoc state :pointer-active? true
                                                            (:picker state)
                                                            (merge {:index index :rotate angle}
                                                              (when (and (= :24h (:format state))
                                                                      (= :hour (:picker state)))
                                                                {:inner? inner?}))))))))

           :on-pointer-move (fn [event]
                              (.stopPropagation event)
                              (when (:pointer-active? state)
                                (let [bounding-rect (-> event .-currentTarget .getBoundingClientRect)
                                      x (- (-> event .-clientX) (.-x bounding-rect) (/ (.-width bounding-rect) 2))
                                      y (+ (- (-> event .-clientY)) (.-y bounding-rect) (/ (.-height bounding-rect) 2))
                                      rotate (+ (Math/atan2 (- x) (- y)) Math/PI)
                                      index (Math/round (* (/ rotate (* 2 Math/PI)) number-of-items) 1)
                                      index (if (= index number-of-items) 0 index)
                                      angle (nth snap-angles index (first snap-angles))]
                                  (let [inner? (< (Math/sqrt (+ (* x x) (* y y)))
                                                 (* 0.66 (/ (.-width bounding-rect) 2)))]
                                    (when (and (or (not (= index (get-in state [(:picker state) :index])))
                                                 (not (= inner? (get-in state [(:picker state) :inner?]))))
                                            (fn? (:on-change props)))
                                      ((:on-change props)
                                       (if (= :minute (:picker state))
                                         (new-time nil index)
                                         (new-time index nil nil inner?))))
                                    (set-state (assoc state (:picker state)
                                                            (merge {:index index :rotate angle}
                                                              (when (and (= :24h (:format state))
                                                                      (= :hour (:picker state)))
                                                                {:inner? inner?}))))))))
           :on-pointer-up   #(set-state (assoc state :pointer-active? false))}
          [:div
           {:style {:transition       (when (:transition? state) "transform calc(2 * var(--transition-duration)) ease")
                    :transform-origin "bottom"
                    :transform        (str "rotate(" rotate "rad)")
                    :position         "absolute"
                    :top              "50%"
                    :left             "50%"}
            :on-transition-end #(set-state (assoc state :transition? false))}
           [:div
            {:style             {:position         "absolute"
                                 :width            "0.125em"
                                 :height           (str "calc(" clock-hand-length "em / 2)")
                                 :background-color "var(--info)"
                                 :margin           (str " calc(" item-size "em / -2) " " calc(" clock-hand-length "em / -4) "
                                                     " calc(" item-size "em / -2) " " calc(" clock-hand-length "em / -4) ")
                                 :top              "50%"
                                 :left             "50%"
                                 :filter           "drop-shadow(0px 0 0.25em black)"
                                 :transform-origin "bottom"
                                 :transform        (str " translateY(calc(" clock-hand-length "em / -2 + " item-size "em / 2)) "
                                                     " translateX(calc(" clock-hand-length "em / 4)) ")}}
            [:div
             {:style {:position         "absolute"
                      :width            (str "calc(" item-size "em)")
                      :height           (str "calc(" item-size "em)")
                      :background-color "hsla(var(--scheme-main-hsl), 1)"
                      :border           "solid 0.125em var(--info)"
                      :border-radius    "50%"
                      :transform        (str " translateY(calc(" item-size "em / -2)) "
                                          " translateX(calc(" item-size "em / -2)) ")}}]
            [:div
             {:style {:position         "absolute"
                      :width            (str "calc(" item-size "em / 4)")
                      :height           (str "calc(" item-size "em / 4)")
                      :background-color "var(--info)"
                      :border-radius    "50%"
                      :bottom           "0"
                      :transform        (str " translateY(calc(" item-size "em / 8 - 0.125em / 2)) "
                                          " translateX(calc(" item-size "em / -8 + 0.125em / 2)) ")}}]]]]
         (map-indexed
           (fn [index item]
             [:div.is-unselectable
              {:key   (str (:picker state) "-" index)
               :style {:position        "absolute"
                       :width           (str item-size "em")
                       :height          (str item-size "em")
                       :margin          (str "calc(" item-size "em / -2)")
                       :display         "flex"
                       :justify-content "center"
                       :align-items     "center"
                       :text-align      "center"
                       :top             "50%"
                       :left            "50%"
                       :transform       (str " rotate(" (- (* index angle) angle-offset) "deg) "
                                          " translate(calc(" circle-size "em / 2)) "
                                          " rotate(" (+ angle-offset (* index angle -1)) "deg)")}}
              [:span {:style {:opacity (if (and (= :24h (:format state)) inner?) "0.5" "1")
                              :transition "opacity var(--transition-duration) ease"}}
               (if (< item 10) (str "0" item) item)]])
           items))
       (map-indexed
         (fn [index item]
           [:div.is-unselectable.fade-in
            {:key   (str (:picker state) "-inner-" index)
             :style {:position        "absolute"
                     :width           (str item-size "em")
                     :height          (str item-size "em")
                     :margin          (str "calc(" item-size "em / -2)")
                     :display         (if (or (not (= :24h (:format state))) (= (:picker state) :minute)) "none" "flex")
                     :justify-content "center"
                     :align-items     "center"
                     :text-align      "center"
                     :top             "50%"
                     :left            "50%"
                     :transform       (str " rotate(" (- (* index angle) angle-offset) "deg) "
                                        " translate(calc(" circle-size "em / 2 - " item-size "em)) "
                                        " rotate(" (+ angle-offset (* index angle -1)) "deg)")}}
            [:span {:style {:opacity (if (and (= :24h (:format state)) inner?) "1" "0.5")
                            :transition "opacity var(--transition-duration) ease"}}
             (if (< item 10) (str "0" item) item)]])
         inner-hours))]))

(defn date-picker-button [& [props]]
  (let [[state set-state] (:use-state props)
        close-fn (fn close-fn []
                   (rf/dispatch [:close-modal :confirm])
                   (set-state (assoc state :date (:date state))))]
    [button/button
     {:class    "is-outlined"
      :style (:style props)
      :on-click #(rf/dispatch [:open-modal :confirm
                               {:background-props {:on-click close-fn}
                                :children    [:f> date-picker-fn
                                              {:value     (:date state)
                                               :on-change (fn [date-time] (set-state (assoc state :date date-time)))}]
                                :footer-props {:style {:background-color "var(--scheme-main)"}}
                                :cancel-props {:on-click close-fn}
                                :confirm-props {:class "is-outlined is-info"
                                                :on-click (fn [] (rf/dispatch [:close-modal :confirm]))}}])}
     [:span (fmt/format-date (:date state))]
     [:span.icon.is-info [:i.far.fa-calendar]]]))

(defn time-picker-button [& [props]]
  (let [[state set-state] (:use-state props)
        close-fn (fn close-fn []
                   (rf/dispatch [:close-modal :confirm])
                   (set-state (assoc state :time (:time state))))]
    [button/button
     {:class    "is-outlined"
      :style (:style props)
      :on-click #(rf/dispatch [:open-modal :confirm
                               {:background-props {:on-click close-fn}
                                :children    [:f> time-picker-fn
                                              {:value     (:time state)
                                               :on-change (fn [time] (set-state (assoc state :time time)))}]
                                :footer-props {:style {:background-color "var(--scheme-main)"}}
                                :cancel-props {:on-click close-fn}
                                :confirm-props {:class "is-outlined is-info"
                                                :on-click (fn [] (rf/dispatch [:close-modal :confirm]))}}])}
     [:span (fmt/format-time (:time state))]
     [:span.icon.is-info [:i.far.fa-clock]]]))

(defn field-control-fn [& argv]
  (let [[state set-state] (use-state {})
        [props children] (common/argv->props-children argv)
        key (:key props)
        label (:label props)
        help-text (:help-text props)
        disabled (:disabled props)
        on-click (:on-click props)
        tags (:tags props)
        tag-set (:set tags)
        tag-data (when (:subscribe-data-path tags) @(rf/subscribe (:subscribe-data-path tags)))
        tag-data-path (or (:data-path tags) [:information :name])
        observed-state-path (:observed-state-path tags)
        observed-state (when observed-state-path nil)]
    [:div.field.is-horizontal
     {:key   key
      :style (merge {:width      "100%"
                     :text-align "start"}
                    (:style props))}
     (when-not (false? label)
       [:div.field-label.is-normal (when label [:label.label label])])
     [:div.field-body
      {:style {:position       "relative"
               :flex-direction (when tags "column")}}
      (when tags
        (into
          [:div.field.is-grouped.is-grouped-multiline]
          (map-indexed
            (fn [index id]
              [:div.control (when (and (= index 0) help-text) {:style {:margin-left "1.25rem"}})
               [:div.tags.has-addons.fade-in
                [:span.tag.is-info
                 (or (when observed-state
                       (get-in (get observed-state id) observed-state-path))
                     (get-in tag-data (into [id] tag-data-path) id))]
                [:span.tag.is-success [:i.fas.fa-check]]]])
            tag-set)))
      [:div.field.has-addons
       {:style {:margin 0}}
       (into
         (into
           [:div.control.is-expanded
            {:style (merge {:display        "flex"
                            :flex-direction "column"}
                           (:control-style props))}]
           children)
         (when (and help-text (:show-help-text? state))
           [[:p.help (if disabled (str help-text ". This is currently disabled") help-text)]]))
       (when (fn? on-click)
         [:div.control
          [button/button
           {:class    "is-danger is-outlined"
            :disabled (or (:reset-button-disabled props) disabled)
            :on-click on-click}
           "Reset"]])]
      (when help-text
        [button/button
         {:class    "is-info is-small is-rounded"
          :tab-index -1
          :style    {:padding     "0"
                     :position "absolute"
                     :top "-0.5rem"
                     :left "-0.75rem"
                     :height "2em"
                     :width "2em"
                     :z-index 1}
          :on-click #(set-state (assoc state :show-help-text? (not (:show-help-text? state))))}
         [:span.icon [:i.fas.fa-info]]])]]))

(defn field-input-control-fn [{:keys [key label help-text reset-button-disabled disabled placeholder checkbox-label value on-change
                                      button-style show-prompt? prompt input-type step button-on-click button-text on-click
                                      class icon-left auto-focus valid-input? input-max-length checked default tags]}]
  (let [[state set-state] (use-state {})]
    [:f> field-control-fn
     {:key key
      :label label
      :help-text help-text
      :on-click on-click
      :reset-button-disabled reset-button-disabled
      :tags tags}
     (cond
       (fn? button-on-click)
       [button/button
        {:class    "is-fullwidth"
         :style    (merge {:margin          "0"
                           :justify-content "space-between"}
                          button-style)
         :disabled disabled
         :on-click button-on-click}
        button-text]
       (= input-type "checkbox")
       [switch {:outer-style {:border "none"}
                      :disabled    disabled
                      :checked     (or checked false)
                      :on-change   on-change}
        checkbox-label]
       :else
       [field-input
        {:input-props      {:type        (if (= input-type "password")
                                           (if (:show-password? state)
                                             "text"
                                             "password")
                                           input-type)
                            :class class
                            :step step
                            :max-length  input-max-length
                            :placeholder (if (= input-type "password") "New password" placeholder)
                            :default     default
                            :value       value
                            :auto-focus  auto-focus
                            :disabled    disabled
                            :on-change   on-change}
         :prompt           prompt
         :prompt-props     (when prompt {:show-prompt? (or show-prompt? (false? valid-input?))})
         :valid-input?     valid-input?
         :icon-left        icon-left
         :icon-right       (if (= input-type "password")
                             (if (:show-password? state)
                               "fas fa-eye-slash"
                               "fas fa-eye")
                             false)
         :icon-right-props {:class    "is-rounded"
                            :style    {:color "var(--blue)"}
                            :on-click #(set-state (assoc state :show-password? (not (:show-password? state))))}}])]))